import { Form, Formik, type FormikHelpers, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { Label, Input, Button } from "reactstrap";
import * as yup from "yup";
import { useState } from "react";

import type { IData } from "./Login";
import useHelpers from "Hooks/useHelpers";
import useUserContext from "Hooks/useUserContext";
import styles from "./Login.module.css";

import procoreWhiteLogo from "Images/Procore_White_Logo.png";
import procoreBlackLogo from "Images/Procore_Black_Logo.png";
import useTheme from "Hooks/useTheme";

interface IProps {
    pageData: IData | undefined;
    onSetWhichModalShown: (whichModalShown: whichModalShown) => void;
}

interface IFormData {
    user_id: string;
    password: string;
    use_company_id: boolean;
}

export default function LoginForm({ pageData, onSetWhichModalShown }: IProps) {
    const [loginFailed, setLoginFailed] = useState(false);
    const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);

    const [userContext, onSetUserContext] = useUserContext();
    const { handleLoginCompanyOkResponse } = useHelpers();
    const { theme } = useTheme();
    const procoreLogo = theme === "dark" ? procoreWhiteLogo : procoreBlackLogo;

    const navigate = useNavigate();

    const validationSchema = yup.object().shape({
        user_id: yup
            .string()
            .required("Required")
            .min(3, "Too short")
            .max(50, "Too long"),
        password: yup.string().required("Required"),
    });

    async function loginDimension(values: IFormData) {
        const companyId = values.use_company_id ? userContext.companyId : "";
        const updatedFormData = {
            ...values,
            company_id: companyId,
        };

        const response = await fetch("/api/login", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedFormData),
        });

        if (response.ok) {
            const data = await response.json();

            onSetUserContext({
                userId: data.user_id,
                username: data.username,
            });
            // If not logging in with the last company, redirect to login_company to pick company
            if (data.company_id === "") {
                navigate("/login_company");
            } else {
                // Otherwise, obtain the JWT and redirect to /run
                const response = await fetch(
                    `/api/login_company?company_id=${userContext.companyId}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();

                if (response.ok) {
                    handleLoginCompanyOkResponse(data);
                }
            }
        } else if (response.status === 423) {
            // Account locked
            const data = await response.json();
            setLockoutMessage(data.detail || "Account is locked. Please try again later.");
            setLoginFailed(false);
        } else {
            setLoginFailed(true);
            setLockoutMessage(null);
        }
    }

    function loginProcore() {
        if (pageData === undefined) {
            return;
        }
        window.location.href = pageData.oauth_url;
    }

    return (
        <Formik
            initialValues={{
                user_id: "",
                password: "",
                use_company_id: true,
            }}
            validationSchema={validationSchema}
            onSubmit={(
                values: {
                    user_id: string;
                    password: string;
                    use_company_id: boolean;
                },
                {
                    setSubmitting,
                }: FormikHelpers<{
                    user_id: string;
                    password: string;
                    use_company_id: boolean;
                }>
            ) => {
                loginDimension(values);
                setSubmitting(false);
            }}
        >
            {({ errors, touched }) => {
                return (
                    <Form className={styles.form}>
                        <div>
                            <Label for="user_id" className={styles.inputLabel}>
                                Username
                            </Label>
                            <Field
                                as={Input}
                                type="text"
                                id="user_id"
                                name="user_id"
                                bsSize="lg"
                                className={styles.input}
                            />
                            {errors.user_id && touched.user_id && (
                                <div className="error mt-1">
                                    {errors.user_id}
                                </div>
                            )}
                        </div>
                        {/* Dimension users */}
                        <div>
                            <Label for="password" className={styles.inputLabel}>
                                Password
                            </Label>
                            <Field
                                as={Input}
                                type="password"
                                id="password"
                                name="password"
                                bsSize="lg"
                                className={styles.input}
                            />
                            {errors.password && touched.password && (
                                <div className="error mt-1">
                                    {errors.password}
                                </div>
                            )}
                        </div>

                        {/* Failed login */}
                        {loginFailed && (
                            <p style={{ color: "red", paddingTop: "5px" }}>
                                Username or password are incorrect.
                            </p>
                        )}

                        {/* Account locked */}
                        {lockoutMessage && (
                            <p style={{ color: "red", paddingTop: "5px" }}>
                                {lockoutMessage}
                            </p>
                        )}

                        <div
                            className={styles.useCompanyIdAndForgotPasswordBox}
                        >
                            <label htmlFor="use_company_id">
                                {userContext.companyId !== "" && (
                                    <div>
                                        <Field
                                            id="use_company_id"
                                            name="use_company_id"
                                            type="checkbox"
                                            className={styles.formCheckInput}
                                        />
                                        <span>
                                            {` Use ${userContext.companyId}`}
                                        </span>
                                    </div>
                                )}
                            </label>
                            <button
                                type="button"
                                className={`${styles.resetPasswordBtn} btn btn-link p-0`}
                                onClick={() =>
                                    onSetWhichModalShown("resetPassword")
                                }
                            >
                                Forgot Password?
                            </button>
                        </div>

                        {/* Procore users */}
                        <Button
                            onClick={loginProcore}
                            color="primary"
                            size="lg"
                            className={styles.procoreBtn}
                        >
                            Sign in with{" "}
                            <img
                                className={styles.procoreLogo}
                                src={procoreLogo}
                                alt="Procore"
                            />
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            size="lg"
                            className={styles.signInBtn}
                            disabled={Boolean(
                                errors.user_id || errors.password
                            )}
                        >
                            Sign in with Dimension
                        </Button>
                    </Form>
                );
            }}
        </Formik>
    );
}
